import { computed } from 'vue'

export default () => {
  const FEATURE_FLAGS = {
    CONTACT_COLLISIONS: 'contact_collisions',
    FORCE_EASTERN_TZ: 'force_eastern_tz',
    EMAIL_NOTIFICATIONS: 'email_notifications',
    CAMPAIGN_EXPORT: 'campaign_export',
    INTEGRATIONS_LEGACY_GUI: 'integrations_gui',
    SEAT_MANAGEMENT: 'seat_management',
    NYLAS_INTEGRATION: 'nylas_integration',
    MEETING_NOTETAKER: 'nylas_notetaker',
    ACCOUNT_TEMPLATES: 'account_templates',
    ACTIVITY_OVERVIEW_SEAT_SELECTION: 'activity_overview_seat_selection',
    DISABLE_TRANSLATIONS: 'disable_translations',
    ENFORCE_OPT_OUT: 'ENFORCE_OPT_OUT',
    DISABLE_PASSWORD: 'disable_password',
    VOICE_CALL: 'voice_call',
    ENABLE_EMOJIS: 'enable_emojis',
    VALIDATE_REASSIGNED_PHONES: 'validate_reassigned_phones',
    DELIVERY_STATUS: 'delivery_status',
    OUT_OF_OFFICE: 'out_of_office',
    VOICE_CALL_TRANSCRIPTIONS: 'voice_call_transcriptions',
    SEND_MMS: 'send_mms',
    CAMPAIGNS_MERGED_UI: 'campaigns_merged_ui',
    HCM_INTEGRATION: 'hcm_integration',
    INTERCOM_CHATBOT: 'intercom_chatbot',
    STRIPE_BILLING: 'stripe_billing',
    INTEGRATION_NAME_RECOGNITION: 'integrations_contacts',
    AI_NAME_RECOGNITION: 'ai_name_recognition',
    TWILIO_NAME_RECOGNITION: 'twilio_autocomplete_callername',
    CONTACT_AUTOCOMPLETION: 'contact_autocompletion'
  }

  const user = useUserStore()
  const currentTenantFeatureFlags = computed(() => user?.currentTenantInfo?.feature_flags || [])

  const hasFeatureFlag = (flag: string) => currentTenantFeatureFlags.value.includes(flag)

  const enableContactCollisions = computed(() => hasFeatureFlag(FEATURE_FLAGS.CONTACT_COLLISIONS))
  const canSelectAllActivityOverviewSeats = computed(() => !!hasFeatureFlag(FEATURE_FLAGS.ACTIVITY_OVERVIEW_SEAT_SELECTION))
  const canUseTranslations = computed(() => !hasFeatureFlag(FEATURE_FLAGS.DISABLE_TRANSLATIONS))
  const enforceAddOptOutMessage = computed(() => !!hasFeatureFlag(FEATURE_FLAGS.ENFORCE_OPT_OUT))
  const canResetPassword = computed(() => !hasFeatureFlag(FEATURE_FLAGS.DISABLE_PASSWORD))
  const canUseVoiceCalls = computed(() => !!hasFeatureFlag(FEATURE_FLAGS.VOICE_CALL))
  const canUseEmojis = computed(() => !!hasFeatureFlag(FEATURE_FLAGS.ENABLE_EMOJIS))
  const hasToForceEasternTz = computed(() => !!hasFeatureFlag(FEATURE_FLAGS.FORCE_EASTERN_TZ))
  const hasToValidateReassignedPhones = computed(() => hasFeatureFlag(FEATURE_FLAGS.VALIDATE_REASSIGNED_PHONES))
  const hasOOOPermission = computed(() => hasFeatureFlag(FEATURE_FLAGS.OUT_OF_OFFICE))
  const hasEmailNotificationsPermission = computed(() => hasFeatureFlag(FEATURE_FLAGS.EMAIL_NOTIFICATIONS))
  const hasAccessToCampaignExport = computed(() => hasFeatureFlag(FEATURE_FLAGS.CAMPAIGN_EXPORT))
  const hasSharedTemplatesFF = computed(() => hasFeatureFlag(FEATURE_FLAGS.ACCOUNT_TEMPLATES))
  const hasSeatManagementFF = computed(() => hasFeatureFlag(FEATURE_FLAGS.SEAT_MANAGEMENT))
  const canShowDeliveryStatus = computed(() => hasFeatureFlag(FEATURE_FLAGS.DELIVERY_STATUS))
  const canSeeTranscriptions = computed(() => hasFeatureFlag(FEATURE_FLAGS.VOICE_CALL_TRANSCRIPTIONS))
  const hasAccessToLegacyIntegrations = computed(() => hasFeatureFlag(FEATURE_FLAGS.INTEGRATIONS_LEGACY_GUI))
  const hasCalendarAccess = computed(() => hasFeatureFlag(FEATURE_FLAGS.NYLAS_INTEGRATION))
  const hasMeetingNotetaker = computed(() => hasFeatureFlag(FEATURE_FLAGS.MEETING_NOTETAKER))
  const canSendMMS = computed(() => hasFeatureFlag(FEATURE_FLAGS.SEND_MMS))
  const showCampaignsMergedUI = computed(() => hasFeatureFlag(FEATURE_FLAGS.CAMPAIGNS_MERGED_UI))
  const hasAccessToHCMIntegrations = computed(() => hasFeatureFlag(FEATURE_FLAGS.HCM_INTEGRATION))
  const hasIntercomChatbot = computed(() => hasFeatureFlag(FEATURE_FLAGS.INTERCOM_CHATBOT))
  const hasBillingAccess = computed(() =>
    hasFeatureFlag(FEATURE_FLAGS.STRIPE_BILLING) && user.readable?.isAdminOrOwner
  )
  const hasIntegrationNameDetection = computed(() => hasFeatureFlag(FEATURE_FLAGS.INTEGRATION_NAME_RECOGNITION))
  const hasAiNameDetection = computed(() => hasFeatureFlag(FEATURE_FLAGS.AI_NAME_RECOGNITION))
  const hasTwilioNameDetection = computed(() => hasFeatureFlag(FEATURE_FLAGS.TWILIO_NAME_RECOGNITION))
  const hasContactAutocompletion = computed(() => hasFeatureFlag(FEATURE_FLAGS.CONTACT_AUTOCOMPLETION))

  return {
    FEATURE_FLAGS,
    enableContactCollisions,
    canSelectAllActivityOverviewSeats,
    canUseTranslations,
    enforceAddOptOutMessage,
    canResetPassword,
    canUseVoiceCalls,
    canUseEmojis,
    hasToForceEasternTz,
    hasToValidateReassignedPhones,
    hasOOOPermission,
    hasEmailNotificationsPermission,
    hasAccessToCampaignExport,
    hasSharedTemplatesFF,
    hasSeatManagementFF,
    canShowDeliveryStatus,
    canSeeTranscriptions,
    hasAccessToLegacyIntegrations,
    hasCalendarAccess,
    hasMeetingNotetaker,
    canSendMMS,
    showCampaignsMergedUI,
    hasAccessToHCMIntegrations,
    hasIntercomChatbot,
    hasBillingAccess,
    hasIntegrationNameDetection,
    hasAiNameDetection,
    hasTwilioNameDetection,
    hasContactAutocompletion
  }
}
